import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

const STATUS_CONFIG = {
  operational: { label: "All Systems Operational", color: "#22c55e", bg: "#052e16" },
  degraded: { label: "Degraded Performance", color: "#f59e0b", bg: "#1c1400" },
  incident: { label: "Active Incident", color: "#ef4444", bg: "#1c0505" },
  maintenance: { label: "Under Maintenance", color: "#3b82f6", bg: "#04091c" },
};

const HEALTH_COLORS = { green: "#22c55e", amber: "#f59e0b", red: "#ef4444" };

export default function PublicStatus() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/status/${slug}`)
      .then(r => setData(r.data))
      .catch(() => setError("Status page not found"));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🔍</div>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Page not found</h1>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
        <div className="text-sm text-gray-400">Loading…</div>
      </div>
    );
  }

  const { page, updates = [], project_summary = {}, overall_status } = data || {};
  if (!page) return (
    <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
      <div className="text-sm text-gray-400">Page not found.</div>
    </div>
  );
  const statusCfg = STATUS_CONFIG[overall_status] || STATUS_CONFIG.operational;

  return (
    <div className="min-h-screen bg-[#f9fafb] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 mb-4">
            <div className="w-6 h-6 rounded bg-[#002FA7] flex items-center justify-center">
              <span className="text-white text-[10px] font-bold">CW</span>
            </div>
            CombinedWork
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{page.title}</h1>
          {page.description && <p className="text-gray-500 text-sm">{page.description}</p>}
        </div>

        {/* Overall status banner */}
        <div
          className="rounded-xl p-5 mb-6 flex items-center gap-4 border"
          style={{ background: statusCfg.bg, borderColor: statusCfg.color + "40" }}
        >
          <div className="w-3 h-3 rounded-full shrink-0 animate-pulse" style={{ background: statusCfg.color }} />
          <div>
            <div className="font-semibold" style={{ color: statusCfg.color }}>{statusCfg.label}</div>
            <div className="text-xs text-gray-400 mt-0.5">Last updated: {updates[0]?.posted_at ? new Date(updates[0].posted_at).toLocaleString() : "Never"}</div>
          </div>
        </div>

        {/* Project summary */}
        {(page.show_health || page.show_tasks) && project_summary && Object.keys(project_summary).length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">Project Overview</h2>
            <div className="flex items-center gap-6 flex-wrap">
              {project_summary.name && (
                <div>
                  <div className="text-xs text-gray-400 mb-1">Project</div>
                  <div className="font-medium text-gray-800">{project_summary.name}</div>
                </div>
              )}
              {project_summary.health && page.show_health && (
                <div>
                  <div className="text-xs text-gray-400 mb-1">Health</div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: HEALTH_COLORS[project_summary.health] || "#22c55e" }} />
                    <span className="text-sm font-medium capitalize text-gray-700">{project_summary.health}</span>
                  </div>
                </div>
              )}
              {typeof project_summary.open_tasks === "number" && page.show_tasks && (
                <div>
                  <div className="text-xs text-gray-400 mb-1">Open tasks</div>
                  <div className="font-medium text-gray-800">{project_summary.open_tasks}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Updates timeline */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700">Recent Updates</h2>
          </div>
          {updates.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-gray-400">No updates yet.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {updates.map(u => {
                const cfg = STATUS_CONFIG[u.status] || STATUS_CONFIG.operational;
                return (
                  <div key={u.update_id} className="px-5 py-4">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: cfg.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-medium text-gray-800 text-sm">{u.title}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full border capitalize font-medium"
                            style={{ color: cfg.color, borderColor: cfg.color + "40", background: cfg.bg }}>
                            {u.status}
                          </span>
                        </div>
                        {u.body && <p className="text-sm text-gray-500 leading-relaxed">{u.body}</p>}
                        <div className="text-xs text-gray-400 mt-2">{new Date(u.posted_at).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-center mt-8 text-xs text-gray-400">
          Powered by <a href="/" className="text-[#002FA7] hover:underline">CombinedWork</a>
        </div>
      </div>
    </div>
  );
}
